import { useState, useEffect, useRef, useCallback, type ChangeEvent } from 'react';
import { ClientEntity } from '../types';
import { db } from '../services/db';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { htmlInicialDoCliente, temConteudoVisivel } from '../lib/site-inicial';

// Helper: Convert RGB/RGBA string to Hex
export const rgbToHex = (rgb: string): string => {
  const sep = rgb.indexOf(",") > -1 ? "," : " ";
  const rgbValues = rgb.substr(4).split(")")[0].split(sep);
  
  let r = (+rgbValues[0]).toString(16),
      g = (+rgbValues[1]).toString(16),
      b = (+rgbValues[2]).toString(16);

  if (r.length === 1) r = "0" + r;
  if (g.length === 1) g = "0" + g;
  if (b.length === 1) b = "0" + b;

  return "#" + r + g + b;
};

// Helper: Check if string is a valid color
export const isValidColor = (str: string) => {
  if (!str) return false;
  if (str === 'rgba(0, 0, 0, 0)') return false;
  if (str === 'transparent') return false;
  if (str.startsWith('rgb')) return true;
  if (str.startsWith('#')) return true;
  return false;
};

export interface ActiveElementData {
  id: string;
  tagName: string;
  bgColor: string;
  textColor: string;
  bgImage: string;
}

export function useIframeEditor(initialClient: ClientEntity | null, slug?: string) {
  const [client, setClient] = useState<ClientEntity | null>(initialClient);
  const [loading, setLoading] = useState(!initialClient);
  const [saving, setSaving] = useState(false);
  const [iframeKey, setIframeKey] = useState(0); 
  const [iframeContent, setIframeContent] = useState<string>("");
  
  // History (Memory) State
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Design System State
  const [detectedColors, setDetectedColors] = useState<{originalRgb: string, currentHex: string, count: number}[]>([]);
  const [detectedImages, setDetectedImages] = useState<{id: string, src: string, type: 'img' | 'bg'}[]>([]);
  
  // Active Element State (for direct editing)
  const [activeElement, setActiveElement] = useState<ActiveElementData | null>(null);

  /**
   * O que o canvas esta mostrando.
   *
   *  - `publicado` — o HTML da versao que esta no ar;
   *  - `rascunho`  — o site existe e NUNCA foi publicado; o conteudo vem do spec;
   *  - `novo`      — o cliente nao tem site, e o canvas e um ponto de partida
   *                  montado com os dados do negocio.
   *
   * Os tres eram indistinguiveis quando o editor abria em branco, e a diferenca
   * importa: publicar sobre `novo` cria o site, sobre `rascunho` publica o que ja
   * estava escrito, e sobre `publicado` substitui o que o visitante ve.
   */
  const [origemDoCanvas, setOrigemDoCanvas] = useState<'publicado' | 'rascunho' | 'novo'>(
    'publicado',
  );
  /**
   * O site tem spec TIPADO (secoes: hero, sobre, servicos...) e nao HTML bruto.
   *
   * Importa porque salvar por este editor achata o spec em `htmlBruto` — o
   * editor trabalha com a string inteira. E o item 2 da secao 4.2 do
   * CONTINUAR-AQUI, e a tela avisa antes em vez de deixar acontecer calado.
   */
  const [specTipado, setSpecTipado] = useState(false);
  /** Falha ao BUSCAR o HTML publicado. Diferente de "nao ha site". */
  const [erroCarga, setErroCarga] = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /**
   * O indice do historico tambem num ref.
   *
   * `pushToHistory` e chamado por listeners que o iframe registrou no `load` —
   * eles seguram a versao da funcao daquele render, com o `historyIndex` daquele
   * momento. Depois de dois undos, editar um texto cortava a pilha no lugar
   * errado e o redo sumia. Com o ref, quem empurra le sempre o indice atual.
   */
  const historyIndexRef = useRef(-1);
  const colorDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [activeImageElementId, setActiveImageElementId] = useState<string | null>(null);

  /**
   * ==========================================================================
   * 1. CARGA INICIAL — por que o canvas abria em branco
   * ==========================================================================
   *
   * O efeito antigo confiava em `client.htmlContent` e parava por ali:
   *
   *     } else if (client && history.length === 0) {
   *       setHistory([client.htmlContent]);   // '' quando nao ha site publicado
   *
   * `htmlContent` vem do artefato no R2 e e VAZIO em dois casos comuns: o
   * cliente nao tem site (nasceu no CRM ou foi importado, `siteId` nulo) e a
   * leitura do HTML falhou. Nos dois, o editor montava um iframe com `srcDoc=""`
   * — dai o canvas branco e a lista de cores com um unico `#000000`, que e a cor
   * de texto herdada de um `<body>` vazio. E como `history.length` deixava de ser
   * zero, nao havia segunda tentativa: a tela ficava assim ate recarregar.
   *
   * Agora: se falta HTML, ele e BUSCADO; se nao existe site nenhum, o canvas
   * abre num rascunho montado com os dados do negocio, marcado como rascunho.
   * O editor nunca mais abre sem nada dentro.
   */
  useEffect(() => {
    let cancelado = false;

    // O corpo vive numa funcao para que o `return` de cada caminho encerre APENAS
    // ele. Escrito direto no efeito, os `return` antecipados pulavam a limpeza —
    // e `cancelado` nunca virava true, entao uma resposta que chegasse depois de
    // trocar de cliente sobrescrevia o canvas do cliente novo.
    const executar = () => {
    /** Monta o canvas com um HTML ja decidido. */
    const iniciar = (
      entidade: ClientEntity,
      html: string,
      origem: 'publicado' | 'rascunho' | 'novo',
    ) => {
      if (cancelado) return;
      setClient({ ...entidade, htmlContent: html });
      setOrigemDoCanvas(origem);
      setHistory([html]);
      setHistoryIndex(0);
      historyIndexRef.current = 0;
      setIframeContent(html);
      setLoading(false);
    };

    /**
     * Decide o que abrir, perguntando a quem sabe.
     *
     * A pergunta que o editor precisa fazer nao e "tem HTML?" — e "por que nao
     * tem?". `db.getSiteHtmlParaEditar` responde com um estado nomeado
     * (publicado / rascunho / sem-site / falha) em vez de uma string vazia, que
     * era o que confundia "R2 fora do ar" com "cliente sem site".
     */
    const abrir = async (entidade: ClientEntity) => {
      const r = await db.getSiteHtmlParaEditar(entidade);
      if (cancelado) return;

      if (r.origem === 'falha') {
        // NAO oferece rascunho aqui. Publicar um rascunho por cima de um site
        // que existe e que so nao conseguimos ler seria perder o site.
        setErroCarga(r.motivo);
        setLoading(false);
        return;
      }

      if (r.origem === 'sem-site') {
        iniciar(entidade, htmlInicialDoCliente(entidade), 'novo');
        return;
      }

      // `rascunho` = site criado e nunca publicado. O conteudo e real (vem do
      // spec), mas ainda nao esta no ar — a tela avisa a diferenca.
      setSpecTipado(r.specTipado === true);
      iniciar(entidade, r.html, r.origem);
    };

    // Ja temos historico: a tela esta em uso, nao remonta por baixo de quem edita.
    if (history.length > 0) return;

    if (client) {
      void abrir(client).catch((e) => {
        console.error('[editor] falha ao carregar o site:', e);
        if (!cancelado) {
          setErroCarga(e instanceof Error ? e.message : 'erro desconhecido');
          setLoading(false);
        }
      });
      return;
    }

    if (slug) {
      void (async () => {
        try {
          const data = await db.getClientBySlug(slug);
          if (cancelado) return;
          if (data) await abrir(data);
          else setLoading(false);
        } catch (e) {
          console.error('[editor] falha ao carregar o cliente:', e);
          if (!cancelado) {
            setErroCarga(e instanceof Error ? e.message : 'erro desconhecido');
            setLoading(false);
          }
        }
      })();
      return;
    }

    setLoading(false);
    };

    executar();
    return () => {
      cancelado = true;
    };
  }, [slug, client, history.length]);

  // History Helper: Push new state to stack
  const pushToHistory = useCallback((newHtml: string, shouldReloadIframe: boolean = false) => {
    setHistory(prev => {
      // `historyIndexRef` e nao `historyIndex`: quem chama isto e um listener que
      // o iframe registrou no `load` e que segura o valor daquele render.
      const newStack = prev.slice(0, historyIndexRef.current + 1);
      if (newStack[newStack.length - 1] === newHtml) return prev; // Avoid duplicate entries
      const proximo = [...newStack, newHtml];
      historyIndexRef.current = proximo.length - 1;
      setHistoryIndex(historyIndexRef.current);
      return proximo;
    });
    setClient(prev => prev ? { ...prev, htmlContent: newHtml } : null);
    if (shouldReloadIframe) {
      setIframeContent(newHtml);
      setIframeKey(k => k + 1);
    }
  }, []);

  const undo = () => {
    if (historyIndex > 0) {
      const prevHtml = history[historyIndex - 1];
      setHistoryIndex(historyIndex - 1);
      historyIndexRef.current = historyIndex - 1;
      setClient(prev => prev ? { ...prev, htmlContent: prevHtml } : null);
      setIframeContent(prevHtml);
      setIframeKey(k => k + 1); // Refresh iframe to show previous state
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const nextHtml = history[historyIndex + 1];
      setHistoryIndex(historyIndex + 1);
      historyIndexRef.current = historyIndex + 1;
      setClient(prev => prev ? { ...prev, htmlContent: nextHtml } : null);
      setIframeContent(nextHtml);
      setIframeKey(k => k + 1); // Refresh iframe to show next state
    }
  };

  /**
   * O `getComputedStyle` do DOCUMENTO CERTO.
   *
   * O codigo chamava `estiloDe(el)` com `el` vindo de dentro do
   * iframe — a janela do PAINEL resolvendo estilo de um elemento de outro
   * documento. Funciona por tolerancia do navegador, nao por contrato: o valor
   * resolvido depende da view do elemento, e e assim que uma cor lida no editor
   * deixa de ser a cor que o site tem. Usar a janela do proprio iframe custa a
   * mesma linha e nao depende de tolerancia.
   */
  const estiloDe = (el: Element): CSSStyleDeclaration => {
    const janela = iframeRef.current?.contentWindow ?? window;
    return janela.getComputedStyle(el);
  };

  // 2. Scan Colors
  const scanSiteColors = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    const colorMap = new Map<string, number>();
    const allElements = doc.getElementsByTagName('*');
    
    for (let i = 0; i < allElements.length; i++) {
      const el = allElements[i];
      const style = estiloDe(el);
      [style.backgroundColor, style.color, style.borderColor].forEach(val => {
        if (isValidColor(val)) {
           const count = colorMap.get(val) || 0;
           colorMap.set(val, count + 1);
        }
      });
    }

    const colorsArray = Array.from(colorMap.entries())
      .map(([rgb, count]) => ({
        originalRgb: rgb,
        currentHex: rgb.startsWith('#') ? rgb : rgbToHex(rgb),
        count
      }))
      .sort((a, b) => b.count - a.count);

    setDetectedColors(colorsArray);
  };

  // 2.5 Scan Images
  const scanSiteImages = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    const images: {id: string, src: string, type: 'img' | 'bg'}[] = [];
    
    // 1. <img> tags
    const imgElements = doc.querySelectorAll('img');
    imgElements.forEach((el, idx) => {
      if (!el.id) el.id = `ditho-img-${idx}`;
      // Ignore tiny tracking pixels or empty sources
      if (el.src && !el.src.startsWith('data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7')) {
         images.push({ id: el.id, src: el.src, type: 'img' });
      }
    });

    // 2. Background images
    const allElements = doc.getElementsByTagName('*');
    let bgIdx = 0;
    for (let i = 0; i < allElements.length; i++) {
      const el = allElements[i] as HTMLElement;
      const style = estiloDe(el);
      if (style.backgroundImage && style.backgroundImage !== 'none' && style.backgroundImage.includes('url(')) {
        if (!el.id) el.id = `ditho-bg-${bgIdx++}`;
        const match = style.backgroundImage.match(/url\(["']?(.*?)["']?\)/);
        if (match && match[1]) {
          images.push({ id: el.id, src: match[1], type: 'bg' });
        }
      }
    }
    setDetectedImages(images);
  };

  // Helper: Get Clean HTML from Iframe (removes editor markers and optionally optimizes)
  const getCleanHtml = (doc: Document, forProduction = false): string | null => {
    if (!doc || !doc.documentElement) return null;
    const clone = doc.documentElement.cloneNode(true) as HTMLElement;
    const editables = clone.querySelectorAll('*[contenteditable]');
    editables.forEach(el => el.removeAttribute('contenteditable'));
    const allEls = clone.getElementsByTagName('*');
    for(let i=0; i<allEls.length; i++) {
        const el = allEls[i] as HTMLElement;
        el.style.outline = '';
        el.style.cursor = '';
        if (el.style.outline === 'none') el.style.outline = '';
        if (el.classList.contains('ditho-active-element')) el.classList.remove('ditho-active-element');
    }
    clone.querySelectorAll('style#editor-temp-styles').forEach(s => s.remove());
    
    // Passo 3: Injeção Mágica de Lazy Load (Apenas na hora de salvar para produção)
    if (forProduction) {
      const images = clone.querySelectorAll('img');
      images.forEach((img, index) => {
        // A primeira imagem geralmente é a logo ou o banner principal. Não damos lazy load nela (First Contentful Paint)
        if (index > 0 && !img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }
      });
    }

    let rawHtml = '<!DOCTYPE html>\n' + clone.outerHTML;

    // Passo 2: Minificação de HTML super segura (Apenas para produção)
    if (forProduction) {
      rawHtml = rawHtml
        .replace(/>\s+</g, '><') // Remove espaços e quebras de linha estritas entre tags
        .trim();
    }

    return rawHtml;
  };

  const getCurrentHtml = (forProduction = false): string => {
    const doc = iframeRef.current?.contentDocument;
    let currentHtml = client?.htmlContent || "";
    if (doc) {
      const cleanHtml = getCleanHtml(doc, forProduction);
      if (cleanHtml) currentHtml = cleanHtml;
    }
    return currentHtml;
  };

  // 3. Inject Editor Scripts
  const handleIframeLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    // Tag elements with their original colors for robust color replacement
    const allElements = doc.getElementsByTagName('*');
    for (let i = 0; i < allElements.length; i++) {
        const el = allElements[i] as HTMLElement;
        const style = estiloDe(el);
        if (isValidColor(style.backgroundColor)) el.dataset.dithoBg = style.backgroundColor;
        if (isValidColor(style.color)) el.dataset.dithoColor = style.color;
        if (isValidColor(style.borderColor)) el.dataset.dithoBorder = style.borderColor;
        
        // Text Editing Detection (More robust)
        let hasDirectText = false;
        for (let j = 0; j < el.childNodes.length; j++) {
            if (el.childNodes[j].nodeType === 3 && el.childNodes[j].textContent?.trim()) {
                hasDirectText = true;
                break;
            }
        }
        
        const tag = el.tagName.toLowerCase();
        const isTextTag = ['h1','h2','h3','h4','h5','h6','p','span','a','li','button','label','td','th','div'].includes(tag);
        const isLogoContainer = el.classList.contains('logo') || el.id.includes('logo') || el.closest('[class*="logo"]') || el.closest('[id*="logo"]');
        
        if (hasDirectText && isTextTag && !isLogoContainer) {
            el.setAttribute('contenteditable', 'true');
            el.style.outline = '1px dashed transparent';
            el.addEventListener('mouseover', (e) => { 
                e.stopPropagation();
                if (!el.classList.contains('ditho-active-element')) el.style.outline = '2px dashed #8b5cf6'; 
            });
            el.addEventListener('mouseout', (e) => { 
                e.stopPropagation();
                if (!el.classList.contains('ditho-active-element')) el.style.outline = '1px dashed transparent'; 
            });
            el.addEventListener('blur', () => {
              const updatedHtml = getCleanHtml(doc);
              if (updatedHtml) pushToHistory(updatedHtml);
            });
        }
    }

    scanSiteColors();
    scanSiteImages();

    // Image & Logo Handling (Robust Detection)
    const makeImageClickable = (el: HTMLElement) => {
      el.style.cursor = 'pointer';
      el.style.outline = '1px dashed transparent';
      
      el.addEventListener('mouseover', (e) => { 
        e.stopPropagation();
        if (!el.classList.contains('ditho-active-element')) el.style.outline = '2px dashed #8b5cf6'; 
      });
      el.addEventListener('mouseout', (e) => { 
        e.stopPropagation();
        if (!el.classList.contains('ditho-active-element')) el.style.outline = '1px dashed transparent'; 
      });

      el.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        setActiveImageElementId(el.id);
        fileInputRef.current?.click();
      });
    };

    // Apply to all images
    doc.querySelectorAll('img').forEach(el => makeImageClickable(el as HTMLElement));
    
    // Apply to all elements with background-image
    const allElementsForBg = doc.getElementsByTagName('*');
    for (let i = 0; i < allElementsForBg.length; i++) {
      const el = allElementsForBg[i] as HTMLElement;
      const style = estiloDe(el);
      if (style.backgroundImage && style.backgroundImage !== 'none' && style.backgroundImage.includes('url(')) {
        makeImageClickable(el);
      }
    }

    // Global Click Listener for Active Element Selection
    doc.body.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (!target || target.tagName.toLowerCase() === 'html' || target.tagName.toLowerCase() === 'body') {
        setActiveElement(null);
        doc.querySelectorAll('.ditho-active-element').forEach(el => {
          el.classList.remove('ditho-active-element');
          (el as HTMLElement).style.outline = '1px dashed transparent';
        });
        return;
      }

      // Prevent default link behavior
      if (target.tagName.toLowerCase() === 'a' || target.closest('a')) {
          e.preventDefault();
      }

      if (!target.id) target.id = `ditho-el-${Math.random().toString(36).substr(2, 9)}`;
      
      // Clear previous active
      doc.querySelectorAll('.ditho-active-element').forEach(el => {
        el.classList.remove('ditho-active-element');
        (el as HTMLElement).style.outline = '1px dashed transparent';
      });

      // Set new active
      target.classList.add('ditho-active-element');
      target.style.outline = '2px solid #3b82f6'; // Blue outline for selected

      const style = estiloDe(target);
      
      setActiveElement({
        id: target.id,
        tagName: target.tagName.toLowerCase(),
        bgColor: style.backgroundColor,
        textColor: style.color,
        bgImage: style.backgroundImage !== 'none' ? style.backgroundImage : ''
      });
    });

    const style = doc.createElement('style');
    style.id = 'editor-temp-styles';
    style.innerHTML = `
      *[contenteditable="true"]:focus { outline: 2px solid #8b5cf6 !important; background: rgba(139, 92, 246, 0.05); }
      .ditho-active-element { outline: 2px solid #3b82f6 !important; outline-offset: 2px; }
    `;
    doc.head.appendChild(style);
  };

  const captureSiteScreenshot = async (): Promise<string | null> => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc || !doc.body) return null;
    try {
      const style = doc.createElement('style');
      style.id = 'temp-screenshot-style';
      style.innerHTML = '* { outline: none !important; cursor: default !important; }';
      doc.head.appendChild(style);
      const canvas = await html2canvas(doc.body, { 
        useCORS: true, 
        scale: 2, // Alta resolução
        logging: false, 
        backgroundColor: '#ffffff',
        windowWidth: 375,
        windowHeight: 667
      });
      doc.getElementById('temp-screenshot-style')?.remove();
      return canvas.toDataURL('image/jpeg', 0.7);
    } catch (e) { return null; }
  };

  const handleSave = async () => {
    if (!client) return;

    const contentToSave = getCurrentHtml(true); // Passa true para otimizar (Minify + Lazy)

    /**
     * A trava que faltava.
     *
     * Com o canvas abrindo em branco, "Publicacao Final" gravava
     * `<html><head></head><body></body></html>` como versao nova — uma pagina
     * vazia por cima de um site publicado, sem aviso nenhum. A string nao e
     * vazia, entao nenhuma checagem de "tem conteudo?" pegava isso.
     */
    if (!temConteudoVisivel(contentToSave)) {
      toast.error('A pagina esta vazia. Nao vou publicar isso por cima do site.');
      return;
    }

    // A segunda metade da mesma trava. Se nao conseguimos LER o site que esta no
    // ar, tambem nao podemos escrever por cima dele: o que fosse publicado agora
    // apagaria um conteudo que ninguem chegou a ver.
    if (erroCarga) {
      toast.error('Nao consegui ler o site atual. Recarregue antes de publicar por cima dele.');
      return;
    }

    setSaving(true);
    try {
      await db.updateClientSite(client.slug, contentToSave);
      setClient(prev => prev ? { ...prev, htmlContent: contentToSave } : null);
      // Publicou: o que esta na tela e o que esta no ar.
      setOrigemDoCanvas('publicado');
      setErroCarga(null);
      toast.success('Site publicado e otimizado!');
    } catch (err) {
      // A mensagem do servidor diz o que houve (403 de dono, site inexistente,
      // R2 fora). Engolir tudo em "Erro ao salvar" era o que fazia a pessoa
      // clicar de novo achando que tinha sido azar.
      console.error('[editor] falha ao publicar:', err);
      const detalhe = err instanceof Error ? err.message : '';
      toast.error(detalhe ? `Nao consegui publicar: ${detalhe}` : 'Erro ao salvar.');
    } finally { setSaving(false); }
  };

  // Update global color
  const updateColor = (originalRgb: string, newHex: string) => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    
    // 1. Visual update (Instant, no flash)
    const allElements = doc.getElementsByTagName('*');
    for (let i = 0; i < allElements.length; i++) {
        const el = allElements[i] as HTMLElement;
        if (el.dataset.dithoBg === originalRgb) el.style.backgroundColor = newHex;
        if (el.dataset.dithoColor === originalRgb) el.style.color = newHex;
        if (el.dataset.dithoBorder === originalRgb) el.style.borderColor = newHex;
    }

    // 2. Update UI state without reloading iframe
    setDetectedColors(prev => prev.map(c => 
      c.originalRgb === originalRgb ? { ...c, currentHex: newHex } : c
    ));

    // 3. Debounce history push to avoid flashing and lag
    if (colorDebounceRef.current) clearTimeout(colorDebounceRef.current);
    colorDebounceRef.current = setTimeout(() => {
       const cleanHtml = getCleanHtml(doc);
       if (cleanHtml) pushToHistory(cleanHtml);
    }, 500);
  };

  // Update specific active element style
  const updateActiveElementStyle = (property: 'backgroundColor' | 'color', value: string) => {
    if (!activeElement) return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const el = doc.getElementById(activeElement.id);
    if (el) {
        (el.style as any)[property] = value;
        setActiveElement(prev => prev ? { 
          ...prev, 
          bgColor: property === 'backgroundColor' ? value : prev.bgColor,
          textColor: property === 'color' ? value : prev.textColor
        } : null);
        
        if (colorDebounceRef.current) clearTimeout(colorDebounceRef.current);
        colorDebounceRef.current = setTimeout(() => {
           const cleanHtml = getCleanHtml(doc);
           if (cleanHtml) pushToHistory(cleanHtml);
        }, 500);
    }
  };

  const triggerImageUpload = (elementId: string) => {
    setActiveImageElementId(elementId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && activeImageElementId) {
      const file = e.target.files[0];
      try {
        const doc = iframeRef.current?.contentDocument;
        const el = doc?.getElementById(activeImageElementId) as HTMLElement;
        if (el && doc) {
            // O destino e obrigatorio para a sessao do PORTAL: `assets.assinarUpload`
            // confere o dono pelo corpo, e sem `clienteId`/`siteId` nao ha como
            // conferir — entao recusa. Com o operador tanto faz, mas mandar o
            // destino sempre e o que faz o mesmo editor servir aos dois.
            const publicUrl = await db.uploadAsset(file, {
              clienteId: client?.id,
              siteId: client?.siteId ?? undefined,
            });
            
            if (el.tagName.toLowerCase() === 'img') {
                (el as HTMLImageElement).src = publicUrl;
            } else {
                el.style.backgroundImage = `url('${publicUrl}')`;
            }
            
            const cleanHtml = getCleanHtml(doc);
            if (cleanHtml) pushToHistory(cleanHtml, false);
            scanSiteImages();
        }
      } catch (err) { toast.error("Erro ao subir imagem."); } finally { if (fileInputRef.current) fileInputRef.current.value = ''; }
    }
  };

  return {
    client,
    loading,
    saving,
    origemDoCanvas,
    specTipado,
    erroCarga,
    iframeKey,
    iframeContent,
    setIframeKey,
    history,
    historyIndex,
    detectedColors,
    detectedImages,
    activeElement,
    iframeRef,
    fileInputRef,
    pushToHistory,
    undo,
    redo,
    handleIframeLoad,
    captureSiteScreenshot,
    handleSave,
    updateColor,
    updateActiveElementStyle,
    triggerImageUpload,
    handleFileChange,
    getCurrentHtml
  };
}
